import { createLogger } from "../helper/logger.js";
import { pdStore } from "../store/mini-rx.store.js";
import { selectAuthUser } from "../store/spa-app-selector.js";
import UniversalRouter from "universal-router";
import { filter, first, firstValueFrom } from "rxjs";
//#region src/router/PdRouterService.ts
var POST_LOGIN_REDIRECT_KEY = "postLoginRedirect";
var routingLogger = createLogger("PdRouterService");
var PdRouterService = class {
	get previousRoute() {
		return this._previousRoute;
	}
	constructor() {
		this.router = null;
		this.onChangeHandlers = [];
		this._currentRoute = "";
		this._previousRoute = "";
		window.addEventListener("popstate", () => {
			this.resolve(location.pathname + location.search);
		});
	}
	/**
	* Setzt die Routen-Konfiguration. Muss vor Verwendung von navigate() aufgerufen werden.
	*/
	configureRoutes(definitions) {
		routingLogger.debug("############ CONFIGURE ROUTING SERVICE ##################", definitions);
		const universalRoutes = definitions.flatMap((def) => {
			return {
				path: def.pattern,
				action: async (context) => {
					if (def.auth || def.roles && def.roles.length > 0) {
						if (!context.userCheck?.loginCheckFinish) {
							routingLogger.debug("Check login is running, wait for result...");
							const authResult = await waitForFinalAuthState();
							routingLogger.debug("Check login is done, go on");
							context.userCheck = authResult;
						}
						if (!context.userCheck?.user) {
							const fallbackRoute = def.authFallback ?? "login";
							const currentPath = context.pathname || window.location.pathname;
							if (currentPath && currentPath !== "/" + fallbackRoute) {
								sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, currentPath);
								routingLogger.debug("Stored redirect route for after login:", currentPath);
							}
							return { redirect: fallbackRoute };
						}
						if (def.roles && def.roles.length > 0 && !def.roles.some((role) => context.userCheck?.user?.claims?.[role] === true)) {
							routingLogger.debug("User lacks required role, redirecting to unauthorized");
							return { redirect: "unauthorized" };
						}
					}
					if (def.action) {
						const result = await def.action();
						if (result && typeof result === "object" && result.route) return {
							...result,
							params: context.params
						};
					}
					return {
						route: def.name,
						params: context.params
					};
				}
			};
		});
		routingLogger.debug("Universal routes transformed: ", universalRoutes);
		this.router = new UniversalRouter(universalRoutes);
		this.resolve(location.pathname + location.search);
	}
	/**
	* Navigiert zu einer neuen Route und triggert resolve()
	*/
	async navigate(path, force = false) {
		const fullPath = new URL(path, window.location.origin).pathname + new URL(path, window.location.origin).search;
		const current = window.location.pathname + window.location.search;
		if (!force && current === fullPath) {
			routingLogger.debug("Navigation skipped (same path):", path);
			return;
		}
		history.pushState({}, "", path);
		await this.resolve(path);
	}
	/**
	* Registriert einen Callback, der bei jeder Routenänderung aufgerufen wird
	*/
	onRouteChange(handler) {
		this.onChangeHandlers.push(handler);
	}
	/**
	* Interne Methode zum Auflösen einer Route und Benachrichtigen aller Listener
	*/
	async resolve(path) {
		if (!this.router) {
			routingLogger.warn("Router not yet configured");
			return;
		}
		const url = new URL(path, window.location.origin);
		const pathname = url.pathname;
		const query = Object.fromEntries(url.searchParams.entries());
		try {
			const userCheck = await firstValueFrom(pdStore().select(selectAuthUser).pipe(first()));
			const result = await this.router.resolve({
				pathname,
				userCheck
			});
			if (!result) {
				routingLogger.warn("No route result for path:", path);
				return;
			}
			if (result.redirect) {
				history.replaceState({}, "", result.redirect);
				await this.resolve(result.redirect);
				return;
			}
			if (typeof result !== "object" || !result.route) {
				routingLogger.warn("Invalid route result:", result);
				return;
			}
			const routeInfo = {
				route: result.route,
				path: pathname,
				params: result.params ?? {},
				query
			};
			if (routeInfo.route !== "login" && !userCheck?.user && sessionStorage.getItem("postLoginRedirect")) sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
			if (this._currentRoute && this._currentRoute !== "login") this._previousRoute = this._currentRoute;
			this._currentRoute = routeInfo.route;
			this.onChangeHandlers.forEach((fn) => fn(routeInfo));
		} catch (err) {
			routingLogger.error("Routing error:", err);
		}
	}
};
async function waitForFinalAuthState() {
	return await firstValueFrom(pdStore().select(selectAuthUser).pipe(filter((res) => res.loginCheckFinish === true), first()));
}
var pdRouterService = new PdRouterService();
//#endregion
export { POST_LOGIN_REDIRECT_KEY, pdRouterService };
